import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { getSafe } from '../../../shared/utility';
import {
  MODEL_SELECTION_RELEASES,
  MODEL_SELECTION_SELECTED_RELEASE,
  TARGET_SELECTION_SELECTED_TARGET,
  TARGET_SELECTION_TARGETS,
  TEST_CONFIG_DYNAMIC_FIELDS,
  TEST_CONFIG_FIELDS,
  TEST_CONFIG_FUSES,
  TEST_CONFIG_STRAPS,
} from '../../../shared/const/Routes/statePaths';
import { Collapse } from 'antd';
import InputField from '../../../components/UI/InputField/InputField';
import {
  CATEGORIES,
  COMMANDS,
  MAIN_SETTINGS,
  TEST_CONTENT_TYPE_SELECT,
  TEST_CONTENT_OBJ,
  TEST_CONTENT_PERSPEC,
} from '../../../shared/const/TestConfiguration/FieldSettings';
import {
  getTestConfigDynamicFields,
  getTestConfigFuses,
  getTestConfigStraps,
} from '../../../store/actions/jobConfiguration/jobConfiguration';
import { InputCheckbox } from '../../../components/UI/InputCheckbox/InputCheckbox';
import SelectField from '../../../components/UI/SelectField/SelectField';
import { DISPLAY_TYPES } from '../../../shared/const/TestConfiguration/FieldTypes';
import {
  FUSE_EDIT_TABLE,
  STRAP_EDIT_TABLE,
} from '../../../shared/const/GenericTableParams/TestConfigurationTables';

import './TestConfiguration.scss';
import CustomEditTable from '../../../components/UI/CustomEditTable/CustomEditTable';
import MultiInputField from '../../../components/UI/MultiInputField/MultiInputField';
import LoadingOverlay from '../../../components/UI/LoadingOverlay/LoadingOverlay';

const TEST_OBJECT_TYPE = {
  PERSPEC: 'perspec',
  OBJ: 'obj',
};

const TestConfiguration = props => {
  const { subProject, setNextDisabled, testFieldsValues, setTestFieldsValues } =
    props;

  const dispatch = useDispatch();

  const selectedTarget = useSelector(state =>
    getSafe(TARGET_SELECTION_SELECTED_TARGET, state)
  );
  const targetData = useSelector(state =>
    getSafe(TARGET_SELECTION_TARGETS, state)
  );
  const selectedRelease = useSelector(state =>
    getSafe(MODEL_SELECTION_SELECTED_RELEASE, state)
  );
  const releaseData = useSelector(state =>
    getSafe(MODEL_SELECTION_RELEASES, state)
  );

  const testFields = useSelector(state => getSafe(TEST_CONFIG_FIELDS, state));
  const dynamicFields = useSelector(state =>
    getSafe(TEST_CONFIG_DYNAMIC_FIELDS, state)
  );

  const fusesData = useSelector(state => getSafe(TEST_CONFIG_FUSES, state));
  const strapsData = useSelector(state => getSafe(TEST_CONFIG_STRAPS, state));

  const [isLoading, setIsLoading] = useState(false);
  const [fusesLoading, setFusesLoading] = useState(false);
  const [strapsLoading, setStrapsLoading] = useState(false);

  useEffect(() => {
    setNextDisabled(true);
    if (!dynamicFields) {
      setIsLoading(true);
      dispatch(getTestConfigDynamicFields(subProject?.projectId));
    }
  }, []);

  useEffect(() => {
    if (dynamicFields) {
      setIsLoading(false);
    }
  }, [dynamicFields]);

  useEffect(() => {
    if (fusesData) {
      setFusesLoading(false);
    }
  }, [fusesData]);

  useEffect(() => {
    if (strapsData) {
      setStrapsLoading(false);
    }
  }, [strapsData]);

  const validateMainSettings = mainSettings => {
    if (mainSettings?.timeout && mainSettings?.seed) {
      return true;
    }
    return false;
  };

  const validateTestContent = testContent => {
    if (!testContent?.testObject) {
      return false;
    }
    if (testContent?.testObject == TEST_OBJECT_TYPE.PERSPEC) {
      if (testContent?.actionName) {
        return true;
      }
    } else if (testContent?.testObject == TEST_OBJECT_TYPE.OBJ) {
      if (testContent?.objPath && testContent?.objName) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (
      validateMainSettings(testFieldsValues.mainSettings) //&&
      //   validateTestContent(testFieldsValues.testContent)
    ) {
      setNextDisabled(false);
    } else {
      setNextDisabled(true);
    }
  }, [testFieldsValues]);

  useEffect(() => {
    if (testFields) {
      Object.keys(testFields).forEach(category => {
        Object.keys(testFields[category]).forEach(fieldName => {
          const value = testFields[category][fieldName].value;
          if (value) {
            onTestFieldValueChange({ category, fieldName, value });
          }
        });
      });
    }

    if (dynamicFields) {
      Object.keys(dynamicFields).forEach(idx => {
        const category = dynamicFields[idx].categoryName;
        Object.keys(dynamicFields[idx].testFields).forEach(fieldName => {
          const value = dynamicFields[idx].testFields[fieldName].value;
          if (value) {
            onTestFieldValueChange({ category, fieldName, value });
          }
        });
      });
    }
  }, [testFields, dynamicFields]);

  const onTestFieldValueChange = ({ category, fieldName, value }) => {
    setTestFieldsValues(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [fieldName]: value,
      },
    }));
  };

  const getTestFieldValue = ({ category, fieldName }) => {
    if (
      !Object.keys(testFieldsValues).includes(category) ||
      !Object.keys(testFieldsValues[category]).includes(fieldName)
    ) {
      return null;
    }
    return testFieldsValues[category][fieldName];
  };

  const getTestContentInnerFields = () => {
    const testObjectType = getTestFieldValue({
      ...TEST_CONTENT_TYPE_SELECT.TEST_OBJECT.actionObj,
    });
    if (testObjectType == TEST_OBJECT_TYPE.OBJ) {
      return renderInputFields(TEST_CONTENT_OBJ, CATEGORIES.TEST_CONTENT);
    }
    if (testObjectType == TEST_OBJECT_TYPE.PERSPEC) {
      return (
        <div className="test-content-perspec-wrapper">
          {/* <label className="test-content-msg">
            Please add relative path prefix
          </label> */}
          {renderInputFields(TEST_CONTENT_PERSPEC, CATEGORIES.TEST_CONTENT)}
        </div>
      );
    }
    return <></>;
  };

  const getTestContent = () => {
    return (
      <div className="inside-collapse-fields-wrapper">
        {renderInputFields(TEST_CONTENT_TYPE_SELECT, CATEGORIES.TEST_CONTENT)}
        {getTestContentInnerFields()}
      </div>
    );
  };

  const renderInputFields = (fieldsMetadata, category) => {
    const DEFAULT_WIDTH = '80%';
    return (
      <div className="inside-collapse-fields-wrapper">
        {Object.keys(fieldsMetadata).map(field => {
          let metadata = fieldsMetadata[field];
          const fieldName = metadata.actionObj
            ? metadata.actionObj.fieldName
            : field;
          switch (metadata.fieldType) {
            case DISPLAY_TYPES.TEXT:
              return (
                <InputField
                  defaultValue={getTestFieldValue({ category, fieldName })}
                  title={metadata.fieldDescription}
                  titlePosition={'left'}
                  width={metadata.width ? metadata.width : DEFAULT_WIDTH}
                  placeholder={
                    metadata.placeholder
                      ? metadata.placeholder
                      : metadata.fieldDescription
                  }
                  onChange={value =>
                    onTestFieldValueChange({ category, fieldName, value })
                  }
                />
              );
            case DISPLAY_TYPES.TEXT_BOX:
              return (
                <MultiInputField
                  defaultValue={getTestFieldValue({ category, fieldName })}
                  width={metadata.width ? metadata.width : DEFAULT_WIDTH}
                  title={metadata.fieldDescription}
                  placeholder={metadata.fieldDescription}
                  onChange={value =>
                    onTestFieldValueChange({ category, fieldName, value })
                  }
                />
              );
            case DISPLAY_TYPES.CHECKBOX:
              return (
                <InputCheckbox
                  title={metadata.fieldDescription}
                  onChange={value =>
                    onTestFieldValueChange({ category, fieldName, value })
                  }
                  width={metadata.width ? metadata.width : DEFAULT_WIDTH}
                  defaultValue={getTestFieldValue({ category, fieldName })}
                  warningText={metadata?.warningText}
                  dialogMessage={metadata?.dialogMessage}
                />
              );
            case DISPLAY_TYPES.DROPDOWN:
              return (
                <SelectField
                  defaultValue={getTestFieldValue({ category, fieldName })}
                  data={metadata.data}
                  displayKey="label"
                  valueKey="value"
                  title={metadata.fieldDescription}
                  titlePosition="left"
                  width={metadata.width ? metadata.width : DEFAULT_WIDTH}
                  onChange={value =>
                    onTestFieldValueChange({ category, fieldName, value })
                  }
                />
              );
            default:
              return <></>;
          }
        })}
      </div>
    );
  };

  const renderDynamicFields = () => {
    if (!dynamicFields || !Object.keys(dynamicFields)) {
      return <></>;
    }
    return (
      <>
        {Object.keys(dynamicFields).map(category => {
          return (
            <Collapse
              className="filter-panel-collapse"
              expandIconPosition="end"
              items={[
                {
                  key: 0,
                  label: dynamicFields[category].categoryName,
                  children: renderInputFields(
                    dynamicFields[category].testFields,
                    category
                  ),
                },
              ]}
            />
          );
        })}
      </>
    );
  };

  const onEditTableRowsChange = (category, nameField, valueField) => rows => {
    const parsedData = {};
    rows.forEach(row => {
      parsedData[row[nameField]] = row[valueField];
    });
    setTestFieldsValues(prev => ({
      ...prev,
      [category]: parsedData,
    }));
  };

  const renderFuses = () => {
    return (
      <LoadingOverlay loading={fusesLoading}>
        <CustomEditTable
          rows={fusesData}
          columns={FUSE_EDIT_TABLE.columns}
          editColumnName={FUSE_EDIT_TABLE.editColumnName}
          defaultValueColumnName={FUSE_EDIT_TABLE.defaultValueColumnName}
          searchColumnName={FUSE_EDIT_TABLE.searchColumnName}
          onEditedRowsChange={onEditTableRowsChange(
            CATEGORIES.FUSES,
            FUSE_EDIT_TABLE.searchColumnName,
            FUSE_EDIT_TABLE.editColumnName
          )}
        />
      </LoadingOverlay>
    );
  };

  const renderStraps = () => {
    return (
      <LoadingOverlay loading={strapsLoading}>
        <CustomEditTable
          rows={strapsData}
          columns={STRAP_EDIT_TABLE.columns}
          editColumnName={STRAP_EDIT_TABLE.editColumnName}
          defaultValueColumnName={STRAP_EDIT_TABLE.defaultValueColumnName}
          searchColumnName={STRAP_EDIT_TABLE.searchColumnName}
          onEditedRowsChange={onEditTableRowsChange(
            CATEGORIES.STRAPS,
            STRAP_EDIT_TABLE.searchColumnName,
            STRAP_EDIT_TABLE.editColumnName
          )}
        />
      </LoadingOverlay>
    );
  };

  const fetchFuses = () => {
    if (fusesData.length == 0) {
      setFusesLoading(true);
      dispatch(
        getTestConfigFuses(
          targetData.find(row => row.TargetId == selectedTarget).id
        )
      );
    }
  };

  const fetchStraps = () => {
    if (strapsData.length == 0) {
      setStrapsLoading(true);
      dispatch(
        getTestConfigStraps(
          targetData.find(row => row.TargetId == selectedTarget).id
        )
      );
    }
  };

  const getChildren = () => {
    return (
      <div className="flex-column row-gap-medium overflow-auto">
        <Collapse
          className="filter-panel-collapse"
          expandIconPosition="end"
          items={[
            {
              key: 0,
              label: 'Main Settings',
              children: renderInputFields(
                MAIN_SETTINGS,
                CATEGORIES.MAIN_SETTINGS
              ),
            },
          ]}
        />
        <Collapse
          className="filter-panel-collapse"
          expandIconPosition="end"
          items={[
            {
              key: 0,
              label: 'Test Content',
              children: getTestContent(),
            },
          ]}
        />
        <Collapse
          className="filter-panel-collapse"
          expandIconPosition="end"
          onChange={() => fetchFuses()}
          items={[
            {
              key: 0,
              label: 'Fuses',
              children: renderFuses(),
            },
          ]}
        />
        <Collapse
          className="filter-panel-collapse"
          expandIconPosition="end"
          onChange={() => fetchStraps()}
          items={[
            {
              key: 0,
              label: 'Straps',
              children: renderStraps(),
            },
          ]}
        />
        <Collapse
          className="filter-panel-collapse"
          expandIconPosition="end"
          items={[
            {
              key: 0,
              label: 'Commands',
              children: renderInputFields(COMMANDS, CATEGORIES.COMMANDS),
            },
          ]}
        />
        {renderDynamicFields()}
      </div>
    );
  };

  return (
    <div className="test-configuration-wrapper">
      <label className="project-sub-title test-configuration-selection-title">
        {subProject?.projectName} -{' '}
        {releaseData.find(row => row.ReleaseId == selectedRelease).ReleaseName}{' '}
        - {targetData.find(row => row.TargetId == selectedTarget).TargetName}
      </label>
      <div className="expander-container">
        <label className="expander-header">
          {subProject?.projectName} -{' '}
          {
            releaseData.find(row => row.ReleaseId == selectedRelease)
              .ReleaseName
          }{' '}
          - {targetData.find(row => row.TargetId == selectedTarget).TargetName}
        </label>
        <LoadingOverlay customClass="filter-panel-content" loading={isLoading}>
          {getChildren()}
        </LoadingOverlay>
      </div>
    </div>
  );
};

export default React.memo(TestConfiguration);
